/**
 * GrantSportIQ — teacher/admin tool to comp any student into a Sport IQ course
 * (Basketball · Football · Soccer · Track) at either "drip" (weekly unlock) or
 * "full" (immediate skip-around access) — no paywall.
 *
 * Mirrors GrantElective's shape and shares the students-lite roster. Calls
 * the existing `/api/academy/admin/sport-iq/comp` backend endpoint.
 */
import React, { useEffect, useState } from 'react';
import { Trophy, Loader2, Search, Sparkles, X } from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('academy_token') || ''}` });

const SPORTS = [
  { key: 'basketball', label: 'The IQ of Basketball' },
  { key: 'football',   label: 'The IQ of Football'   },
  { key: 'soccer',     label: 'The IQ of Soccer'     },
  { key: 'track',      label: 'The IQ of Track'      },
];

const GrantSportIQ = () => {
  const [students, setStudents] = useState([]);
  const [q, setQ] = useState('');
  const [selectedStudent, setSelectedStudent] = useState('');
  const [sport, setSport] = useState('basketball');
  const [mode, setMode] = useState('full');   // "full" = all 6 weeks unlocked immediately · "drip" = 1/wk
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [log, setLog] = useState([]);          // recent grants (this session)

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get('/academy/admin/students-lite', { headers: authHeaders() });
        setStudents(r.data.students || []);
      } catch { /* silent */ }
    })();
  }, []);

  const filtered = students.filter(
    (s) => !q || (s.name || '').toLowerCase().includes(q.toLowerCase()) || (s.email || '').toLowerCase().includes(q.toLowerCase())
  );
  const student = students.find((s) => s.id === selectedStudent);

  const grant = async () => {
    if (!student) { toast.error('Pick a student first'); return; }
    setBusy(true);
    try {
      const r = await api.post('/academy/admin/sport-iq/comp',
        { email: student.email, sport, mode, note: reason },
        { headers: authHeaders() });
      toast.success(`${SPORTS.find(s => s.key === sport).label} · ${mode.toUpperCase()} · granted to ${student.name}`);
      setLog((prev) => [{ student: student.name, sport, mode, at: new Date().toLocaleTimeString() }, ...prev].slice(0, 5));
      setReason('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not grant Sport IQ access');
    } finally { setBusy(false); }
  };

  const revoke = async () => {
    if (!student) { toast.error('Pick a student first'); return; }
    if (!window.confirm(`Revoke ${sport} access for ${student.name}?`)) return;
    setBusy(true);
    try {
      await api.post('/academy/admin/sport-iq/revoke',
        { email: student.email, sport },
        { headers: authHeaders() });
      toast.success(`${sport} access revoked for ${student.name}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not revoke');
    } finally { setBusy(false); }
  };

  return (
    <div className="border-2 border-orange-500/30 bg-orange-500/[0.03] p-5" data-testid="grant-sport-iq-panel">
      <div className="flex items-center gap-2 mb-4">
        <Trophy className="w-4 h-4 text-orange-400" />
        <span className="text-xs font-black tracking-[0.2em] uppercase text-orange-400">Grant Sport IQ Access · No Paywall</span>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {/* Student picker */}
        <div>
          <label className="block text-[9px] uppercase tracking-widest text-zinc-500 font-black mb-1">Student</label>
          <div className="relative mb-2">
            <Search className="w-3.5 h-3.5 text-zinc-600 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name or email…"
              className="w-full bg-black border-2 border-zinc-800 pl-8 pr-3 py-2 text-sm text-white outline-none focus:border-orange-500"
              data-testid="sport-iq-search"
            />
          </div>
          <select
            value={selectedStudent}
            onChange={(e) => setSelectedStudent(e.target.value)}
            className="w-full bg-black border-2 border-zinc-800 px-3 py-2 text-sm text-white outline-none focus:border-orange-500"
            data-testid="sport-iq-student-select"
          >
            <option value="">Select a student…</option>
            {filtered.map((s) => (
              <option key={s.id} value={s.id}>{s.name} · {s.email}</option>
            ))}
          </select>
          {student && (
            <div className="mt-2 text-[11px] text-zinc-400">Selected: <span className="text-orange-300 font-black">{student.name}</span> · {student.email}</div>
          )}
        </div>

        {/* Sport + access mode */}
        <div>
          <label className="block text-[9px] uppercase tracking-widest text-zinc-500 font-black mb-1">Sport IQ Course</label>
          <select
            value={sport}
            onChange={(e) => setSport(e.target.value)}
            className="w-full bg-black border-2 border-zinc-800 px-3 py-2 text-sm text-white outline-none focus:border-orange-500 mb-3"
            data-testid="sport-iq-sport-select"
          >
            {SPORTS.map((s) => (<option key={s.key} value={s.key}>{s.label}</option>))}
          </select>

          <label className="block text-[9px] uppercase tracking-widest text-zinc-500 font-black mb-1">Access Mode</label>
          <div className="grid grid-cols-2 gap-2 mb-3">
            <button onClick={() => setMode('full')}
              className={`text-[10px] font-black uppercase tracking-widest px-3 py-2.5 border-2 ${mode === 'full' ? 'bg-orange-500 text-black border-orange-500' : 'text-zinc-400 border-zinc-800 hover:border-zinc-600'}`}
              data-testid="sport-iq-mode-full">
              Full · All 6 Weeks
            </button>
            <button onClick={() => setMode('drip')}
              className={`text-[10px] font-black uppercase tracking-widest px-3 py-2.5 border-2 ${mode === 'drip' ? 'bg-orange-500 text-black border-orange-500' : 'text-zinc-400 border-zinc-800 hover:border-zinc-600'}`}
              data-testid="sport-iq-mode-drip">
              Drip · 1 / Week
            </button>
          </div>

          <label className="block text-[9px] uppercase tracking-widest text-zinc-500 font-black mb-1">Reason (optional)</label>
          <input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Enrolled student comp, team pilot"
            className="w-full bg-black border-2 border-zinc-800 px-3 py-2 text-sm text-white outline-none focus:border-orange-500"
            data-testid="sport-iq-reason"
          />
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-3">
        <button
          onClick={grant}
          disabled={busy || !selectedStudent}
          className="flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-400 disabled:bg-zinc-800 disabled:text-zinc-600 text-black px-5 py-2.5 text-[11px] font-black tracking-[0.2em] uppercase transition-colors"
          data-testid="sport-iq-grant-btn"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Sparkles className="w-4 h-4" /> Grant Sport IQ</>}
        </button>
        <button
          onClick={revoke}
          disabled={busy || !selectedStudent}
          className="flex items-center justify-center gap-2 border-2 border-red-500/60 text-red-300 hover:bg-red-500/10 disabled:opacity-40 px-5 py-2.5 text-[11px] font-black tracking-[0.2em] uppercase transition-colors"
          data-testid="sport-iq-revoke-btn"
        >
          <X className="w-4 h-4" /> Revoke
        </button>
      </div>

      {log.length > 0 && (
        <div className="mt-4 border-t border-zinc-800 pt-3" data-testid="sport-iq-log">
          <div className="text-[9px] uppercase tracking-widest text-zinc-500 font-black mb-2">This Session</div>
          {log.map((l, i) => (
            <div key={i} className="text-[11px] text-zinc-400 flex items-center gap-2">
              <span className="text-emerald-400">✓</span>
              <span className="text-orange-300 font-black">{l.student}</span>
              <span className="text-zinc-500">·</span>
              <span>{l.sport}</span>
              <span className="text-zinc-500">·</span>
              <span className="uppercase text-[9px]">{l.mode}</span>
              <span className="text-zinc-600 ml-auto">{l.at}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default GrantSportIQ;
